import axios from 'axios';
import { BLOG_ENDPOINTS } from './endpoints';
import { TOKEN_TYPE, getTokenFromCookies, clearAllTokens } from '../../../lib/cookies';
import type {
  Blog,
  BlogPermissions,
  Category,
  Post,
  PostEditorData,
  PostImage,
  CreateBlogRequest,
  UpdateBlogRequest,
  CreateCategoryRequest,
  UpdateCategoryRequest,
  CreatePostRequest,
  UpdatePostRequest,
  AddPostImageRequest,
  UpdatePostImageRequest,
  PaginationParams,
  ApiResponse,
} from '../types';

// Base URL for blog service
const BLOG_BASE_URL = import.meta.env.VITE_BLOG_API_BASE_URL || 'https://blog-worker.khanhthinh-45a4.workers.dev';

// Create dedicated axios instance for blog API
const blogAxios = axios.create({
  baseURL: BLOG_BASE_URL,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests if available
blogAxios.interceptors.request.use((config) => {
  const token = getTokenFromCookies(TOKEN_TYPE.ACCESS_TOKEN);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor - handle authentication errors
blogAxios.interceptors.response.use(
  (response) => response,
  (error) => {
    console.log('Blog API error:', error.response?.status, error.response?.data);

    // Handle 401 errors
    if (error.response?.status === 401) {
      const hasToken = getTokenFromCookies(TOKEN_TYPE.ACCESS_TOKEN);
      if (hasToken) {
        console.log('Clearing tokens due to 401');
        clearAllTokens();
        window.location.href = '/auth/login';
      }
    }

    return Promise.reject(error);
  }
);

export const blogAPI = {
  // ========== PUBLIC ENDPOINTS ==========
  public: {
    // Get blog by ID
    getBlog: async (id: number): Promise<ApiResponse<Blog>> => {
      const response = await blogAxios.get(BLOG_ENDPOINTS.public.getBlog(id));
      return response.data;
    },

    // Get blog by slug
    getBlogBySlug: async (slug: string): Promise<ApiResponse<Blog>> => {
      const response = await blogAxios.get(BLOG_ENDPOINTS.public.getBlogBySlug(slug));
      return response.data;
    },

    // List categories by blog
    getBlogCategories: async (
      blogId: number,
      params?: PaginationParams
    ): Promise<ApiResponse<Category[]>> => {
      const response = await blogAxios.get(BLOG_ENDPOINTS.public.getBlogCategories(blogId), {
        params,
      });
      return response.data;
    },

    // List posts by blog
    getBlogPosts: async (
      blogId: number,
      params?: PaginationParams & { status?: string }
    ): Promise<ApiResponse<Post[]>> => {
      const response = await blogAxios.get(BLOG_ENDPOINTS.public.getBlogPosts(blogId), {
        params,
      });
      return response.data;
    },

    // List posts by category
    getCategoryPosts: async (
      categoryId: number,
      params?: PaginationParams
    ): Promise<ApiResponse<Post[]>> => {
      const response = await blogAxios.get(BLOG_ENDPOINTS.public.getCategoryPosts(categoryId), {
        params,
      });
      return response.data;
    },

    // Get post by ID
    getPost: async (id: number): Promise<ApiResponse<Post>> => {
      const response = await blogAxios.get(BLOG_ENDPOINTS.public.getPost(id));
      return response.data;
    },

    // Get post by slug
    getPostBySlug: async (slug: string): Promise<ApiResponse<Post>> => {
      const response = await blogAxios.get(BLOG_ENDPOINTS.public.getPostBySlug(slug));
      return response.data;
    },

    // Get post images
    getPostImages: async (postId: number): Promise<ApiResponse<PostImage[]>> => {
      const response = await blogAxios.get(BLOG_ENDPOINTS.public.getPostImages(postId));
      return response.data;
    },
  },

  // ========== ADMIN ENDPOINTS ==========
  admin: {
    // --- Blogs ---
    createBlog: async (data: CreateBlogRequest): Promise<ApiResponse<Blog>> => {
      const response = await blogAxios.post(BLOG_ENDPOINTS.admin.createBlog, data);
      return response.data;
    },

    listMyBlogs: async (params?: PaginationParams): Promise<ApiResponse<Blog[]>> => {
      const response = await blogAxios.get(BLOG_ENDPOINTS.admin.listMyBlogs, { params });
      return response.data;
    },

    getBlog: async (id: number): Promise<ApiResponse<Blog>> => {
      const response = await blogAxios.get(BLOG_ENDPOINTS.admin.getBlog(id));
      return response.data;
    },

    updateBlog: async (id: number, data: UpdateBlogRequest): Promise<ApiResponse<Blog>> => {
      const response = await blogAxios.put(BLOG_ENDPOINTS.admin.updateBlog(id), data);
      return response.data;
    },

    deleteBlog: async (id: number): Promise<ApiResponse<void>> => {
      const response = await blogAxios.delete(BLOG_ENDPOINTS.admin.deleteBlog(id));
      return response.data;
    },

    getBlogPermissions: async (blogId: number): Promise<ApiResponse<BlogPermissions>> => {
      const response = await blogAxios.get(BLOG_ENDPOINTS.admin.getBlogPermissions(blogId));
      return response.data;
    },

    uploadBlogMedia: async (blogId: number, file: File, postId?: number, purpose?: 'banner' | 'post-media'): Promise<ApiResponse<{ fileId: string; url: string; ownerId: string }>> => {
      const form = new FormData();
      form.append('file', file);
      if (postId) form.append('post_id', String(postId));
      if (purpose) form.append('purpose', purpose);
      const response = await blogAxios.post(BLOG_ENDPOINTS.admin.uploadBlogMedia(blogId), form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    },

    deleteBlogMedia: async (blogId: number, fileId: string): Promise<ApiResponse<void>> => {
      const response = await blogAxios.delete(BLOG_ENDPOINTS.admin.deleteBlogMedia(blogId, fileId));
      return response.data;
    },

    getBlogDriveStatus: async (blogId: number): Promise<ApiResponse<{ connected: boolean }>> => {
      const response = await blogAxios.get(BLOG_ENDPOINTS.admin.getBlogDriveStatus(blogId));
      return response.data;
    },

    addBlogAdmin: async (blogId: number, email: string): Promise<ApiResponse<void>> => {
      const response = await blogAxios.post(BLOG_ENDPOINTS.admin.addBlogAdmin(blogId), {
        email,
      });
      return response.data;
    },

    removeBlogAdmin: async (blogId: number, email: string): Promise<ApiResponse<void>> => {
      const response = await blogAxios.delete(BLOG_ENDPOINTS.admin.removeBlogAdmin(blogId), {
        data: { email },
      });
      return response.data;
    },

    transferBlogOwnership: async (
      blogId: number,
      email: string
    ): Promise<ApiResponse<Blog>> => {
      const response = await blogAxios.put(
        BLOG_ENDPOINTS.admin.transferBlogOwnership(blogId),
        { email }
      );
      return response.data;
    },

    getBlogCategories: async (
      blogId: number,
      params?: PaginationParams
    ): Promise<ApiResponse<Category[]>> => {
      const response = await blogAxios.get(BLOG_ENDPOINTS.admin.getBlogCategories(blogId), {
        params,
      });
      return response.data;
    },

    getBlogPosts: async (
      blogId: number,
      params?: PaginationParams & { status?: string }
    ): Promise<ApiResponse<Post[]>> => {
      const response = await blogAxios.get(BLOG_ENDPOINTS.admin.getBlogPosts(blogId), {
        params,
      });
      return response.data;
    },

    // --- Categories ---
    createCategory: async (data: CreateCategoryRequest): Promise<ApiResponse<Category>> => {
      const response = await blogAxios.post(BLOG_ENDPOINTS.admin.createCategory, data);
      return response.data;
    },

    getCategory: async (id: number): Promise<ApiResponse<Category>> => {
      const response = await blogAxios.get(BLOG_ENDPOINTS.admin.getCategory(id));
      return response.data;
    },

    updateCategory: async (
      id: number,
      data: UpdateCategoryRequest
    ): Promise<ApiResponse<Category>> => {
      const response = await blogAxios.put(BLOG_ENDPOINTS.admin.updateCategory(id), data);
      return response.data;
    },

    deleteCategory: async (id: number): Promise<ApiResponse<void>> => {
      const response = await blogAxios.delete(BLOG_ENDPOINTS.admin.deleteCategory(id));
      return response.data;
    },

    // --- Posts ---
    createPost: async (data: CreatePostRequest): Promise<ApiResponse<Post>> => {
      const response = await blogAxios.post(BLOG_ENDPOINTS.admin.createPost, data);
      return response.data;
    },

    createDraftPost: async (blogId: number): Promise<ApiResponse<PostEditorData>> => {
      const response = await blogAxios.post(BLOG_ENDPOINTS.admin.createDraftPost(blogId));
      return response.data;
    },

    listMyPosts: async (params?: PaginationParams): Promise<ApiResponse<Post[]>> => {
      const response = await blogAxios.get(BLOG_ENDPOINTS.admin.listMyPosts, { params });
      return response.data;
    },

    getPost: async (id: number): Promise<ApiResponse<Post>> => {
      const response = await blogAxios.get(BLOG_ENDPOINTS.admin.getPost(id));
      return response.data;
    },

    updatePost: async (id: number, data: UpdatePostRequest): Promise<ApiResponse<Post>> => {
      const response = await blogAxios.put(BLOG_ENDPOINTS.admin.updatePost(id), data);
      return response.data;
    },

    publishPost: async (id: number): Promise<ApiResponse<PostEditorData>> => {
      const response = await blogAxios.post(BLOG_ENDPOINTS.admin.publishPost(id));
      return response.data;
    },

    deletePost: async (id: number): Promise<ApiResponse<void>> => {
      const response = await blogAxios.delete(BLOG_ENDPOINTS.admin.deletePost(id));
      return response.data;
    },

    // --- Post Images ---
    getPostImages: async (postId: number): Promise<ApiResponse<PostImage[]>> => {
      const response = await blogAxios.get(BLOG_ENDPOINTS.admin.getPostImages(postId));
      return response.data;
    },

    addPostImage: async (
      postId: number,
      data: AddPostImageRequest
    ): Promise<ApiResponse<PostImage>> => {
      const response = await blogAxios.post(BLOG_ENDPOINTS.admin.addPostImage(postId), data);
      return response.data;
    },

    addPostImagesBatch: async (
      postId: number,
      images: AddPostImageRequest[]
    ): Promise<ApiResponse<PostImage[]>> => {
      const response = await blogAxios.post(BLOG_ENDPOINTS.admin.addPostImagesBatch(postId), { images });
      return response.data;
    },

    removePostImage: async (postId: number, imageId: number): Promise<ApiResponse<void>> => {
      const response = await blogAxios.delete(
        BLOG_ENDPOINTS.admin.removePostImage(postId, imageId)
      );
      return response.data;
    },

    updatePostImage: async (
      postId: number,
      imageId: number,
      data: UpdatePostImageRequest
    ): Promise<ApiResponse<PostImage>> => {
      const response = await blogAxios.put(
        BLOG_ENDPOINTS.admin.updatePostImage(postId, imageId),
        data
      );
      return response.data;
    },
  },
};
